<template>
  <div class="article-list-page">
    <section class="article-list-hero">
      <p class="eyebrow">Articles</p>
      <h1>全部文章</h1>
      <p>按分类或关键词寻找你想读的内容。</p>
    </section>

    <section class="article-search" aria-label="文章搜索">
      <el-select v-model="categoryId" clearable placeholder="全部分类" @change="filterByCategory">
        <el-option v-for="cat in categories" :key="cat.id" :label="cat.name" :value="cat.id" />
      </el-select>
      <el-input
        v-model.trim="keywordInput"
        clearable
        placeholder="搜索文章标题"
        @keyup.enter.native="searchArticles"
      />
      <el-button type="primary" @click="searchArticles">搜索</el-button>
      <el-button @click="resetSearch">重置</el-button>
    </section>

    <div v-if="loading" class="home-state">正在加载文章...</div>

    <section class="content-grid article-card-grid">
      <article
        class="post-card article-card"
        v-for="post in posts"
        :key="post.id"
        :data-article-id="post.id"
      >
        <img
          class="article-card-cover"
          :src="post.coverImage"
          :alt="`${post.title}封面`"
          @load="restoreSavedScrollOnce"
        >
        <div class="article-card-meta">
          <time>{{ post.updatedAt }}</time>
          <span
            v-if="post.categoryName"
            class="article-card-category"
            :class="`tone-${post.categoryTone}`"
          >
            {{ post.categoryName }}
          </span>
        </div>
        <div class="article-card-body">
          <h2>{{ post.title }}</h2>
          <p v-if="post.author" class="article-card-author">作者：{{ post.author }}</p>
          <p>{{ post.summary }}</p>
        </div>
        <a
          class="article-card-link"
          :href="`/articles/${post.id}`"
          @click.prevent="openArticle(post.id, $event)"
        >
          阅读全文
        </a>
      </article>
    </section>

    <div v-if="!loading && posts.length === 0" class="home-state">
      还没有发布文章
    </div>

    <section class="blog-pagination" aria-label="文章分页">
      <el-pagination
        background
        :current-page="page"
        layout="total, sizes, prev, pager, next"
        :page-sizes="[12, 24, 36, 48]"
        :page-size="size"
        :total="total"
        @current-change="changePage"
        @size-change="onSizeChange"
      />
    </section>
  </div>
</template>

<script>
import { fetchPublicArticles } from '@/services/publicArticles';
import { fetchPublicCategories } from '@/services/categories';
import { getArticleCover } from '@/utils/articles';

const ARTICLE_LIST_STATE_KEY = 'article_list_state';
const ARTICLE_LIST_RESTORE_QUERY = 'restore';
const LIST_RESTORE_DELAY = 120;

export default {
  name: 'ArticleList',
  data() {
    return {
      posts: [],
      loading: false,
      keyword: '',
      keywordInput: '',
      categories: [],
      categoryId: null,
      categorySlug: '',
      page: 1,
      size: 12,
      total: 0,
      hasRestoredScroll: false,
      restoreTimer: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.size));
    },
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyQueryState();
        this.loadArticles();
      },
      deep: true,
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prepareScrollRestore(from);
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.prepareScrollRestore(from);
    next();
  },
  created() {
    this.applyQueryState();
    this.loadArticles();
    this.loadCategories();
  },
  beforeDestroy() {
    if (this.restoreTimer) {
      window.clearTimeout(this.restoreTimer);
    }
  },
  methods: {
    getSavedListState() {
      try {
        return JSON.parse(window.sessionStorage.getItem(ARTICLE_LIST_STATE_KEY) || 'null');
      } catch (error) {
        return null;
      }
    },
    clearPendingScrollRestore() {
      const savedState = this.getSavedListState();

      if (!savedState?.restorePending) {
        return;
      }

      this.saveListState({
        ...savedState,
        restorePending: false,
      });
    },
    prepareScrollRestore(from) {
      if (from?.name === 'ArticleDetail') {
        return;
      }

      this.clearPendingScrollRestore();
    },
    getCleanListFullPath() {
      const query = {
        ...(this.$route.query || {}),
      };

      delete query[ARTICLE_LIST_RESTORE_QUERY];

      return this.$router.resolve({
        path: this.$route.path,
        query,
      }).route.fullPath;
    },
    rememberListState(articleId, event) {
      const card = event?.currentTarget?.closest?.('.article-card');
      const cardTop = card?.getBoundingClientRect?.().top || 0;
      const restoreToken = `${Date.now()}-${articleId}`;

      window.sessionStorage.setItem(ARTICLE_LIST_STATE_KEY, JSON.stringify({
        fullPath: this.getCleanListFullPath(),
        articlePath: `/articles/${articleId}`,
        articleId: String(articleId),
        cardTop,
        scrollY: window.scrollY || window.pageYOffset || 0,
        restoreToken,
        restorePending: false,
      }));

      return restoreToken;
    },
    openArticle(articleId, event) {
      const restoreToken = this.rememberListState(articleId, event);

      this.$router.push({
        path: `/articles/${articleId}`,
        query: {
          from: 'list',
          returnTo: this.getCleanListFullPath(),
          restore: restoreToken,
        },
      });
    },
    saveListState(state) {
      window.sessionStorage.setItem(ARTICLE_LIST_STATE_KEY, JSON.stringify(state));
    },
    consumeSavedScrollState() {
      const savedState = this.getSavedListState();
      const restoreToken = this.$route.query?.[ARTICLE_LIST_RESTORE_QUERY];

      if (
        !savedState
        || !savedState.restorePending
        || !restoreToken
        || savedState.restoreToken !== restoreToken
        || savedState.fullPath !== this.getCleanListFullPath()
      ) {
        return null;
      }

      this.saveListState({
        ...savedState,
        restorePending: false,
        restoredAt: Date.now(),
      });

      return savedState;
    },
    restoreSavedScrollOnce() {
      if (this.hasRestoredScroll) {
        return;
      }

      const savedState = this.consumeSavedScrollState();

      if (!savedState) {
        return;
      }

      this.hasRestoredScroll = true;

      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            const target = document.querySelector(`[data-article-id="${savedState.articleId}"]`);

            if (target) {
              const targetTop = target.getBoundingClientRect().top + window.pageYOffset;
              const desiredTop = targetTop - (Number(savedState.cardTop) || 0);

              window.scrollTo({
                top: Math.max(0, desiredTop),
                left: 0,
                behavior: 'auto',
              });
              return;
            }

            window.scrollTo({
              top: Number(savedState.scrollY) || 0,
              left: 0,
              behavior: 'auto',
            });
          });
        });
      });
    },
    applyQueryState() {
      const query = this.$route.query || {};
      const nextPage = Number(query.page) || 1;
      const nextSize = Number(query.size) || 12;

      this.page = Math.max(1, nextPage);
      this.size = [12, 24, 36, 48].includes(nextSize) ? nextSize : 12;
      this.keyword = typeof query.keyword === 'string' ? query.keyword : '';
      this.keywordInput = this.keyword;
      this.categoryId = query.categoryId ? Number(query.categoryId) : null;
      this.categorySlug = typeof query.categorySlug === 'string' ? query.categorySlug : '';
    },
    updateRouteQuery(overrides = {}) {
      const nextState = {
        page: this.page,
        size: this.size,
        keyword: this.keyword,
        categoryId: this.categoryId,
        categorySlug: this.categorySlug,
        ...overrides,
      };
      const query = {};

      if (nextState.page && Number(nextState.page) !== 1) {
        query.page = String(nextState.page);
      }

      if (nextState.size && Number(nextState.size) !== 12) {
        query.size = String(nextState.size);
      }

      if (nextState.keyword) {
        query.keyword = nextState.keyword;
      }

      if (nextState.categoryId) {
        query.categoryId = String(nextState.categoryId);
      } else if (nextState.categorySlug) {
        query.categorySlug = nextState.categorySlug;
      }

      const current = this.$route.query || {};
      const sameQuery = Object.keys(query).length === Object.keys(current).length
        && Object.keys(query).every((key) => query[key] === current[key]);

      if (!sameQuery) {
        this.$router.replace({
          path: this.$route.path,
          query,
        });
        return true;
      }

      return false;
    },
    normalizeArticle(item) {
      const categoryName = item.category?.name || '';

      return {
        id: item.id || item.articleId,
        title: item.title || '未命名文章',
        summary: item.summary || '',
        author: item.author || '',
        coverImage: getArticleCover(item.coverImage),
        updatedAt: item.publishedAt || item.updatedAt || item.updateTime || item.createdAt || '-',
        visible: item.visible !== false,
        categoryName,
        categoryTone: this.getCategoryTone(categoryName),
      };
    },
    getCategoryTone(categoryName) {
      const name = categoryName || '';
      let hash = 0;

      for (let index = 0; index < name.length; index += 1) {
        hash += name.charCodeAt(index);
      }

      return hash % 6;
    },
    extractArticles(body) {
      const data = body?.data ?? body;
      const list = Array.isArray(data)
        ? data
        : data?.records
          || data?.list
          || data?.content
          || data?.items
          || data?.rows
          || body?.records
          || body?.list;

      return Array.isArray(list)
        ? list.map(this.normalizeArticle).filter((article) => article.visible)
        : [];
    },
    extractTotal(body, fallback) {
      const data = body?.data ?? body;

      return data?.total
        || data?.totalElements
        || data?.count
        || body?.total
        || body?.count
        || fallback;
    },
    extractErrorMessage(error) {
      return error.response?.data?.message
        || error.response?.data?.msg
        || error.message
        || '文章加载失败，请稍后再试';
    },
    async loadArticles() {
      this.loading = true;
      this.hasRestoredScroll = false;
      if (this.restoreTimer) {
        window.clearTimeout(this.restoreTimer);
      }

      try {
        const params = {
          page: this.page,
          size: this.size,
          keyword: this.keyword,
        };
        if (this.categoryId) {
          params.categoryId = this.categoryId;
        } else if (this.categorySlug) {
          params.categorySlug = this.categorySlug;
        }
        const response = await fetchPublicArticles(params);
        const posts = this.extractArticles(response.data);
        this.posts = posts;
        this.total = this.extractTotal(response.data, posts.length);
        this.restoreTimer = window.setTimeout(() => {
          this.restoreSavedScrollOnce();
        }, LIST_RESTORE_DELAY);
      } catch (error) {
        this.posts = [];
        this.total = 0;
        this.$message.error(this.extractErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    searchArticles() {
      this.keyword = this.keywordInput;
      this.page = 1;
      if (!this.updateRouteQuery()) {
        this.loadArticles();
      }
    },
    filterByCategory() {
      this.page = 1;
      if (!this.updateRouteQuery()) {
        this.loadArticles();
      }
    },
    async loadCategories() {
      try {
        const response = await fetchPublicCategories();
        const data = response?.data?.data || response?.data;
        this.categories = Array.isArray(data) ? data : [];
      } catch (e) {
        // silent
      }
    },
    resetSearch() {
      this.keyword = '';
      this.keywordInput = '';
      this.categoryId = null;
      this.categorySlug = '';
      this.page = 1;
      if (!this.updateRouteQuery()) {
        this.loadArticles();
      }
    },
    changePage(page) {
      this.page = page;
      if (!this.updateRouteQuery()) {
        this.loadArticles();
      }
    },
    onSizeChange(size) {
      this.size = size;
      this.page = 1;
      if (!this.updateRouteQuery()) {
        this.loadArticles();
      }
    },
  },
};
</script>
